/**
 * Manus API Key Manager
 * Quản lý nhiều API keys và phân phối theo cơ chế round-robin
 */

import { ref, get, set, push, remove, update } from "firebase/database";
import { db } from "../firebase/config";
import { debugLog } from "./manusConfig";

const MANUS_KEYS_PATH = "manus/apiKeys";
const ROTATION_PATH = "manus/keyRotation";

/**
 * Initialize env key nếu chưa có trong Firebase
 */
const initializeEnvKey = async () => {
  try {
    const envKey = process.env.REACT_APP_MANUS_API_KEY;
    if (!envKey) {
      debugLog("No env API key found, skipping initialization");
      return;
    }

    const envKeyRef = ref(db, `${MANUS_KEYS_PATH}/env_key`);
    const snapshot = await get(envKeyRef);

    if (!snapshot.exists()) {
      await set(envKeyRef, {
        key: envKey,
        source: "environment",
        enabled: true,
        createdAt: new Date().toISOString(),
        description: "API key from environment variables",
      });
      debugLog("Initialized env key in Firebase");
    }
  } catch (error) {
    console.error("Error initializing env key:", error);
  }
};

/**
 * Lấy API key theo ID cụ thể
 * @param {string} keyId - ID của key cần lấy
 * @returns {Promise<{id: string, key: string, source: string, enabled: boolean} | null>}
 */
export const getKeyById = async (keyId) => {
  try {
    if (!keyId) {
      return null;
    }

    await initializeEnvKey();

    const keyRef = ref(db, `${MANUS_KEYS_PATH}/${keyId}`);
    const snapshot = await get(keyRef);

    if (!snapshot.exists()) {
      debugLog(`Key not found: ${keyId}`);
      return null;
    }

    const keyData = snapshot.val();
    return {
      id: keyId,
      ...keyData,
    };
  } catch (error) {
    console.error(`Error getting key by ID (${keyId}):`, error);
    return null;
  }
};

/**
 * Lấy tất cả API keys từ Firebase
 * @returns {Promise<Array>} Array of {id, key, source, enabled, createdAt, description}
 */
export const getAllKeys = async () => {
  try {
    await initializeEnvKey();

    const keysRef = ref(db, MANUS_KEYS_PATH);
    const snapshot = await get(keysRef);

    if (!snapshot.exists()) {
      return [];
    }

    const keysData = snapshot.val();
    const keys = Object.entries(keysData).map(([id, data]) => ({
      id,
      ...data,
    }));

    // Sort: env key first, then admin keys by creation time
    keys.sort((a, b) => {
      if (a.source === "environment") return -1;
      if (b.source === "environment") return 1;
      return new Date(a.createdAt) - new Date(b.createdAt);
    });

    return keys;
  } catch (error) {
    console.error("Error getting all keys:", error);
    return [];
  }
};

/**
 * Lấy chỉ các keys đang enabled
 * @returns {Promise<Array>} Array of enabled keys
 */
export const getEnabledKeys = async () => {
  const allKeys = await getAllKeys();
  return allKeys.filter((k) => k.enabled);
};

/**
 * Lấy API key tiếp theo với priority: env key trước, sau đó round-robin admin keys
 * @returns {Promise<{key: string, keyId: string, source: string} | null>}
 */
export const getNextKey = async () => {
  try {
    const enabledKeys = await getEnabledKeys();

    if (enabledKeys.length === 0) {
      console.warn("No enabled Manus API keys found");
      return null;
    }

    // Lấy current index cho round-robin rotation
    const rotationRef = ref(db, ROTATION_PATH);
    const rotationSnapshot = await get(rotationRef);

    let currentIndex = 0;
    if (rotationSnapshot.exists()) {
      currentIndex = rotationSnapshot.val()?.currentIndex || 0;
    }

    // ✅ Round-robin qua TẤT CẢ enabled keys (env + admin)
    const nextIndex = currentIndex % enabledKeys.length;
    const selectedKey = enabledKeys[nextIndex];

    // Update rotation index cho lần sau
    const newIndex = (currentIndex + 1) % enabledKeys.length;
    await set(rotationRef, {
      currentIndex: newIndex,
      lastRotatedAt: new Date().toISOString(),
      totalEnabledKeys: enabledKeys.length,
    });

    debugLog("Using key (round-robin)", {
      keyId: selectedKey.id,
      source: selectedKey.source,
      currentIndex: nextIndex,
      nextIndex: newIndex,
      totalKeys: enabledKeys.length,
    });

    return {
      key: selectedKey.key,
      keyId: selectedKey.id,
      source: selectedKey.source,
    };
  } catch (error) {
    console.error("Error getting next key:", error);
    return null;
  }
};

/**
 * Thêm API key mới (admin only)
 * @param {string} apiKey - API key string
 * @param {string} description - Mô tả key
 * @param {string} createdBy - User ID của admin
 * @returns {Promise<{success: boolean, keyId?: string, message?: string}>}
 */
export const addKey = async (apiKey, description = "", createdBy = "admin") => {
  try {
    if (!apiKey || !apiKey.trim()) {
      return { success: false, message: "API key không được để trống" };
    }

    // Validate format (basic check)
    if (!apiKey.startsWith("sk-")) {
      return {
        success: false,
        message: "API key phải bắt đầu bằng 'sk-'",
      };
    }

    // Check duplicate
    const allKeys = await getAllKeys();
    const isDuplicate = allKeys.some((k) => k.key === apiKey);
    if (isDuplicate) {
      return { success: false, message: "API key đã tồn tại" };
    }

    // Add new key
    const keysRef = ref(db, MANUS_KEYS_PATH);
    const newKeyRef = push(keysRef);

    await set(newKeyRef, {
      key: apiKey,
      source: "admin",
      enabled: true,
      createdAt: new Date().toISOString(),
      createdBy,
      description: description || "Admin API key",
    });

    debugLog("Added new Manus API key", { keyId: newKeyRef.key });

    return {
      success: true,
      keyId: newKeyRef.key,
      message: "Thêm API key thành công",
    };
  } catch (error) {
    console.error("Error adding key:", error);
    return {
      success: false,
      message: error.message || "Lỗi khi thêm API key",
    };
  }
};

/**
 * Xóa API key (admin only)
 * @param {string} keyId - ID của key cần xóa
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const removeKey = async (keyId) => {
  try {
    if (!keyId) {
      return { success: false, message: "Key ID không hợp lệ" };
    }

    // Không cho phép xóa env key
    if (keyId === "env_key") {
      return {
        success: false,
        message: "Không thể xóa environment key",
      };
    }

    const keyRef = ref(db, `${MANUS_KEYS_PATH}/${keyId}`);
    await remove(keyRef);

    debugLog("Removed Manus API key", { keyId });

    return { success: true, message: "Xóa API key thành công" };
  } catch (error) {
    console.error("Error removing key:", error);
    return {
      success: false,
      message: error.message || "Lỗi khi xóa API key",
    };
  }
};

/**
 * Bật/tắt API key (admin only)
 * @param {string} keyId - ID của key
 * @param {boolean} enabled - true để bật, false để tắt
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const toggleKey = async (keyId, enabled) => {
  try {
    if (!keyId) {
      return { success: false, message: "Key ID không hợp lệ" };
    }

    const keyRef = ref(db, `${MANUS_KEYS_PATH}/${keyId}`);
    const snapshot = await get(keyRef);

    if (!snapshot.exists()) {
      return { success: false, message: "Key không tồn tại" };
    }

    await update(keyRef, {
      enabled,
      updatedAt: new Date().toISOString(),
    });

    debugLog("Toggled Manus API key", { keyId, enabled });

    return {
      success: true,
      message: enabled ? "Đã bật API key" : "Đã tắt API key",
    };
  } catch (error) {
    console.error("Error toggling key:", error);
    return {
      success: false,
      message: error.message || "Lỗi khi thay đổi trạng thái key",
    };
  }
};

/**
 * Update description của key
 * @param {string} keyId - ID của key
 * @param {string} description - Mô tả mới
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const updateKeyDescription = async (keyId, description) => {
  try {
    if (!keyId) {
      return { success: false, message: "Key ID không hợp lệ" };
    }

    const keyRef = ref(db, `${MANUS_KEYS_PATH}/${keyId}`);
    const snapshot = await get(keyRef);

    if (!snapshot.exists()) {
      return { success: false, message: "Key không tồn tại" };
    }

    await update(keyRef, {
      description,
      updatedAt: new Date().toISOString(),
    });

    return { success: true, message: "Cập nhật mô tả thành công" };
  } catch (error) {
    console.error("Error updating description:", error);
    return {
      success: false,
      message: error.message || "Lỗi khi cập nhật mô tả",
    };
  }
};

/**
 * Lấy thông tin rotation hiện tại
 * @returns {Promise<{currentIndex: number, lastRotatedAt: string, totalEnabledKeys: number}>}
 */
export const getRotationInfo = async () => {
  try {
    const rotationRef = ref(db, ROTATION_PATH);
    const snapshot = await get(rotationRef);

    if (!snapshot.exists()) {
      return {
        currentIndex: 0,
        lastRotatedAt: null,
        totalEnabledKeys: 0,
      };
    }

    return snapshot.val();
  } catch (error) {
    console.error("Error getting rotation info:", error);
    return {
      currentIndex: 0,
      lastRotatedAt: null,
      totalEnabledKeys: 0,
    };
  }
};

/**
 * Reset rotation index về 0
 * @returns {Promise<{success: boolean}>}
 */
export const resetRotation = async () => {
  try {
    const rotationRef = ref(db, ROTATION_PATH);
    await set(rotationRef, {
      currentIndex: 0,
      lastRotatedAt: new Date().toISOString(),
      totalEnabledKeys: 0,
    });

    debugLog("Reset rotation index to 0");

    return { success: true };
  } catch (error) {
    console.error("Error resetting rotation:", error);
    return { success: false };
  }
};

const manusKeyManager = {
  getAllKeys,
  getEnabledKeys,
  getNextKey,
  addKey,
  removeKey,
  toggleKey,
  updateKeyDescription,
  getRotationInfo,
  resetRotation,
};

export default manusKeyManager;
